import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { purchaseApi, inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-PUR-03.01–03 — Goods receipts.
 *
 * List / detail / create flow. Create picks an open PO; the form
 * pre-populates receivable lines with their open quantity. Per-line
 * fields for batch number (if batch-tracked), serial numbers (if
 * serial-tracked), rejection quantity.
 */
export function GrnsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['purchase', 'grns', statusFilter],
    queryFn: () => purchaseApi.listGrns({ status: statusFilter || undefined, limit: 200 }),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let draft = 0, confirmed = 0, rejected = 0, totalValue = 0;
    for (const g of rows) {
      if (g.status === 'draft') draft++;
      else if (g.status === 'confirmed') { confirmed++; totalValue += parseFloat(g.total_value ?? '0') || 0; }
      else if (g.status === 'rejected') rejected++;
    }
    return { total: rows.length, draft, confirmed, rejected, totalValue };
  })();

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'rejected',  label: 'Rejected' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Purchase"
        title="Goods receipts"
        subtitle="Receive inbound goods against open POs. Confirming a GRN posts stock movements and accrued-payable journal entries."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New GRN
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Draft" value={kpis.draft}
          accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="Confirmed" value={kpis.confirmed} accent="sage" />
        <KpiTile label="Received value" value={`${fmtCurrency(kpis.totalValue)}`} />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
            !listQuery.data?.length ? (
              <EmptyState
                message={statusFilter ? `No ${statusFilter} GRNs` : 'No goods receipts yet'}
                hint={statusFilter ? 'Try a different filter.' : 'Create a GRN to log receipt of inbound goods against an open PO.'}
                action={!statusFilter && (
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>
                    + New GRN
                  </button>
                )}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>GRN #</th>
                      <th>PO</th>
                      <th>Supplier</th>
                      <th>Status</th>
                      <th className="text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(listQuery.data ?? []).map((g: any) => (
                      <tr key={g.id}
                          onClick={() => setSelected(g.id)}
                          className={`cursor-pointer ${selected === g.id ? '!bg-canvas-subtle' : ''}`}>
                        <td className="font-mono text-xs font-semibold text-ink-900">{g.grn_number}</td>
                        <td className="font-mono text-xs text-ink-600">{g.po_number}</td>
                        <td className="text-sm text-ink-700">{g.supplier_name}</td>
                        <td><StatusBadge status={g.status} /></td>
                        <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                          ${parseFloat(g.total_value ?? '0').toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a GRN"
              hint="Click a row on the left to view line items and confirm/reject actions."
            />
          ) : (
            <GrnDetail
              grnId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['purchase', 'grns'] })}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateGrnModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['purchase', 'grns'] });
            setShowCreate(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'confirmed' ? 'bg-sage-50 text-sage-700 ring-sage-200' :
    status === 'rejected' ? 'bg-red-50 text-red-700 ring-red-200' :
    'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function GrnDetail({ grnId, onChanged }: { grnId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['purchase', 'grn', grnId],
    queryFn: () => purchaseApi.getGrn(grnId),
  });

  const [showReject, setShowReject] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const confirmMut = useMutation({
    mutationFn: () => purchaseApi.confirmGrn(grnId),
    onSuccess: (data) => {
      toast.success(`GRN ${data.grnNumber} confirmed. ${fmtCurrency(data.confirmedValue)} in. JE: ${data.journalEntryNumber ?? 'n/a'}. PO ${data.poFullyReceived ? 'fully received' : 'partially received'}.`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['purchase', 'grn', grnId] });
      onChanged();
      setShowConfirm(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rejectMut = useMutation({
    mutationFn: (reason: string) => purchaseApi.rejectGrn(grnId, reason),
    onSuccess: () => {
      toast.success('GRN rejected');
      qc.invalidateQueries({ queryKey: ['purchase', 'grn', grnId] });
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (detailQuery.isLoading) return <LoadingState rows={4} />;
  if (detailQuery.error) return <ErrorState error={detailQuery.error} />;
  const g = detailQuery.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{g.grn_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{g.supplier_name}</div>
          <div className="text-xs text-ink-500 mt-1">
            PO <span className="font-mono font-semibold text-ink-700">{g.po_number}</span>
          </div>
          <div className="text-xs text-ink-500 mt-0.5">
            → {g.warehouse_code} {g.warehouse_name}
          </div>
          {g.supplier_delivery_note && (
            <div className="text-xs text-ink-500 mt-0.5">
              Delivery note <span className="font-mono">{g.supplier_delivery_note}</span>
            </div>
          )}
          {g.notes && <div className="text-xs text-ink-500 italic mt-1 leading-relaxed">{g.notes}</div>}
        </div>
        <StatusBadge status={g.status} />
      </div>

      {/* Timeline */}
      <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1 text-xs text-ink-600">
        <div>
          <span className="text-ink-400">Received:</span>{' '}
          {new Date(g.created_at).toLocaleString()} · {g.received_by_name}
        </div>
        {g.confirmed_at && (
          <div className="text-sage-700">
            <span className="text-sage-600">Confirmed:</span>{' '}
            {new Date(g.confirmed_at).toLocaleString()} · {g.confirmed_by_name}
          </div>
        )}
        {g.journal_entry_number && (
          <div>
            <span className="text-ink-400">JE:</span>{' '}
            <span className="font-mono font-semibold">{g.journal_entry_number}</span>
          </div>
        )}
      </div>

      {/* Rejection alert */}
      {g.rejected_at && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          <div className="font-semibold">Rejected</div>
          <div className="mt-0.5">{new Date(g.rejected_at).toLocaleString()} · {g.rejected_by_name}</div>
          {g.rejection_reason && <div className="mt-1">{g.rejection_reason}</div>}
        </div>
      )}

      {/* Line items */}
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>Product</th>
              <th className="text-right">Received</th>
              <th className="text-right">Rejected</th>
              <th className="text-right">Unit cost</th>
              <th>Batch / Serials</th>
            </tr>
          </thead>
          <tbody>
            {g.lines.map((l: any) => (
              <tr key={l.id}>
                <td className="font-mono text-2xs text-ink-500">{l.po_line_no}</td>
                <td>
                  <div className="font-medium text-ink-900">{l.product_name}</div>
                  <div className="font-mono text-2xs text-ink-500 mt-0.5">{l.sku}</div>
                </td>
                <td className="text-right font-mono tabular-nums">{parseFloat(l.quantity).toFixed(0)}</td>
                <td className="text-right font-mono tabular-nums">
                  {parseFloat(l.rejected_qty) > 0
                    ? <span className="text-danger font-semibold">{parseFloat(l.rejected_qty).toFixed(0)}</span>
                    : <span className="text-ink-400">—</span>}
                </td>
                <td className="text-right font-mono tabular-nums">{fmtCurrency(parseFloat(l.unit_cost))}</td>
                <td className="text-xs">
                  {l.batch_number && (
                    <div className="text-ink-700">
                      Batch <span className="font-mono font-semibold">{l.batch_number}</span>
                    </div>
                  )}
                  {l.expiry_date && (
                    <div className="text-ink-500 mt-0.5">
                      Exp {new Date(l.expiry_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                  {l.serial_numbers && l.serial_numbers.length > 0 && (
                    <div className="font-mono text-2xs text-ink-500 mt-0.5">
                      {l.serial_numbers.slice(0, 3).join(', ')}
                      {l.serial_numbers.length > 3 && ` +${l.serial_numbers.length - 3}`}
                    </div>
                  )}
                  {!l.batch_number && !l.expiry_date && (!l.serial_numbers || l.serial_numbers.length === 0) && (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {g.status === 'draft' && (
          <>
            <button
              className="btn-secondary"
              disabled={rejectMut.isPending}
              onClick={() => setShowReject(true)}
            >Reject GRN</button>
            <button
              className="btn-primary"
              disabled={confirmMut.isPending}
              onClick={() => setShowConfirm(true)}
            >
              {confirmMut.isPending ? 'Confirming…' : 'Confirm GRN'}
            </button>
          </>
        )}
      </div>

      {showReject && (
        <RejectGrnDialog
          grnNumber={g.grn_number}
          loading={rejectMut.isPending}
          onClose={() => setShowReject(false)}
          onConfirm={(reason) => rejectMut.mutate(reason)}
        />
      )}
      {showConfirm && (
        <ConfirmGrnDialog
          grnNumber={g.grn_number}
          loading={confirmMut.isPending}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => confirmMut.mutate()}
        />
      )}
    </div>
  );
}

function ConfirmGrnDialog({
  grnNumber, loading, onClose, onConfirm,
}: { grnNumber: string; loading: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Purchase · Confirm</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Confirm goods receipt?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Confirming <span className="font-mono font-semibold text-ink-900">{grnNumber}</span> will
          post stock movements at the received unit cost and create an AP journal entry. The PO
          will update to partially-received or fully-received status.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading} onClick={onConfirm}>
          {loading ? 'Confirming…' : 'Confirm GRN'}
        </button>
      </div>
    </Modal>
  );
}

function RejectGrnDialog({
  grnNumber, loading, onClose, onConfirm,
}: { grnNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Purchase · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject goods receipt?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Reject <span className="font-mono font-semibold text-ink-900">{grnNumber}</span>?
          No stock or journal entries will be posted. The reason stays on file for audit.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Quantities don't match delivery note — supplier to re-send" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}>
          {loading ? 'Rejecting…' : 'Reject GRN'}
        </button>
      </div>
    </Modal>
  );
}

function CreateGrnModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [poId, setPoId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Array<{
    poLineId: string; productId: string; productName?: string; sku?: string;
    poQty: number; openQty: number;
    receivedQty: number; rejectedQty: number;
    unitCost: number;
    isBatchTracked: boolean; batchNumber: string; expiryDate: string;
    isSerialTracked: boolean; serialNumbersText: string;
  }>>([]);

  const poListQuery = useQuery({
    queryKey: ['purchase', 'orders', 'receivable'],
    queryFn: () => purchaseApi.listOrders({ limit: 100 }),
  });
  const receivablePos = (poListQuery.data ?? []).filter((po: any) =>
    ['approved', 'partially_received'].includes(po.status));

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });

  const openLinesQuery = useQuery({
    queryKey: ['purchase', 'po', poId, 'open-lines'],
    queryFn: () => purchaseApi.getOpenPoLines(poId),
    enabled: !!poId,
  });

  // When open lines load, pre-populate the lines form
  const loadOpenLines = () => {
    if (!openLinesQuery.data) return;
    setLines((openLinesQuery.data ?? []).map((l: any) => ({
      poLineId: l.po_line_id,
      productId: l.product_id,
      productName: l.product_name,
      sku: l.sku,
      poQty: parseFloat(l.quantity),
      openQty: parseFloat(l.open_qty),
      receivedQty: parseFloat(l.open_qty), // default: receive all open
      rejectedQty: 0,
      unitCost: parseFloat(l.unit_price),
      isBatchTracked: l.is_batch_tracked,
      batchNumber: '',
      expiryDate: '',
      isSerialTracked: l.is_serial_tracked,
      serialNumbersText: '',
    })));
  };

  const createMut = useMutation({
    mutationFn: () => purchaseApi.createGrn({
      poId, warehouseId,
      supplierDeliveryNote: deliveryNote || undefined,
      notes: notes || undefined,
      lines: lines.filter((l) => l.receivedQty + l.rejectedQty > 0).map((l) => {
        const serials = l.serialNumbersText.split(/\s+/).map((s) => s.trim()).filter(Boolean);
        return {
          poLineId: l.poLineId,
          productId: l.productId,
          quantity: l.receivedQty,
          unitCost: l.unitCost,
          batchNumber: l.isBatchTracked ? (l.batchNumber || null) : null,
          expiryDate: l.isBatchTracked ? (l.expiryDate || null) : null,
          serialNumbers: l.isSerialTracked && serials.length > 0 ? serials : undefined,
          rejectedQty: l.rejectedQty,
        };
      }),
    }),
    onSuccess: (data) => {
      toast.success(`GRN ${data.grnNumber} created as draft. Confirm to post stock.`);
      onCreated(data.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const disabledReason = !poId ? 'Select a purchase order to continue.'
    : !warehouseId ? 'Select a receiving warehouse.'
    : lines.length === 0 ? 'This purchase order has no open lines.'
    : !lines.some((l) => l.receivedQty + l.rejectedQty > 0)
      ? 'Enter a receive or reject quantity on at least one line.'
    : (() => {
        const bad = lines.find((l) => {
          if (!l.isSerialTracked || l.receivedQty === 0) return false;
          const n = l.serialNumbersText.split(/\s+/).map((s) => s.trim()).filter(Boolean).length;
          return n !== Math.floor(l.receivedQty);
        });
        if (!bad) return '';
        const n = bad.serialNumbersText.split(/\s+/).map((s) => s.trim()).filter(Boolean).length;
        return `${bad.sku}: ${Math.floor(bad.receivedQty)} serial number(s) required, ${n} entered.`;
      })();

  const canSubmit = poId && warehouseId && lines.length > 0
    && lines.some((l) => l.receivedQty + l.rejectedQty > 0)
    && lines.every((l) => {
      if (!l.isSerialTracked) return true;
      const serials = l.serialNumbersText.split(/\s+/).map((s) => s.trim()).filter(Boolean);
      return l.receivedQty === 0 || serials.length === Math.floor(l.receivedQty);
    });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New goods receipt</h2>
        <p className="text-sm text-ink-500 mt-1">
          Record what was delivered from a purchase order. Creates a draft — confirm to post stock and AP.
        </p>
      </div>

      <div className="px-6 pt-4 pb-3 grid grid-cols-3 gap-3 flex-shrink-0">
        <div>
          <label className="label">Purchase order <span className="text-danger">*</span></label>
          <select className="input" value={poId} onChange={(e) => { setPoId(e.target.value); setLines([]); }}>
            <option value="">Select PO…</option>
            {receivablePos.map((po: any) => (
              <option key={po.id} value={po.id}>
                {po.po_number} — {po.supplier_name} (${parseFloat(po.grand_total).toFixed(2)})
              </option>
            ))}
          </select>
          <div className="text-2xs text-ink-400 mt-1">Approved / partially received</div>
        </div>
        <div>
          <label className="label">Receiving at warehouse <span className="text-danger">*</span></label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select…</option>
            {whQuery.data?.map((w: any) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Supplier delivery note <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="DN-#" />
        </div>
      </div>

      {poId && openLinesQuery.data && lines.length === 0 && (
        <div className="px-6 pb-3 flex-shrink-0">
          <button
            className="text-sm font-semibold text-sage-700 hover:text-sage-800 transition-colors"
            onClick={loadOpenLines}
          >
            + Load {openLinesQuery.data.length} receivable line{openLinesQuery.data.length === 1 ? '' : 's'} from PO
          </button>
        </div>
      )}

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="eyebrow text-ink-500 mb-2">Lines</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          {lines.length === 0 ? (
            <div className="p-8 text-center text-xs text-ink-400">
              {!poId ? 'Select a purchase order to begin.' :
               openLinesQuery.isLoading ? 'Loading open lines…' :
               openLinesQuery.data?.length === 0 ? 'All PO lines have been fully received.' :
               'Click "Load receivable lines" above.'}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right w-20">Open</th>
                  <th className="text-right w-20">Receive</th>
                  <th className="text-right w-20">Reject</th>
                  <th className="text-right w-24">Unit cost</th>
                  <th>Batch / Serials</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <div className="font-mono text-2xs text-ink-500">{l.sku}</div>
                      <div className="font-medium text-ink-900 text-xs">{l.productName}</div>
                    </td>
                    <td className="text-right text-xs text-ink-500 font-mono tabular-nums">{l.openQty.toFixed(0)}</td>
                    <td className="text-right">
                      <input type="number" min={0} max={l.openQty} step={1}
                        className="input w-20 text-right tabular-nums" value={l.receivedQty}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(l.openQty, parseFloat(e.target.value) || 0));
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, receivedQty: v } : x));
                        }}
                      />
                    </td>
                    <td className="text-right">
                      <input type="number" min={0} step={1}
                        className="input w-20 text-right tabular-nums" value={l.rejectedQty}
                        onChange={(e) => {
                          const v = Math.max(0, parseFloat(e.target.value) || 0);
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, rejectedQty: v } : x));
                        }}
                      />
                    </td>
                    <td className="text-right">
                      <input type="number" min={0} step={0.01}
                        className="input w-24 text-right tabular-nums" value={l.unitCost}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitCost: v } : x));
                        }}
                      />
                    </td>
                    <td>
                      {l.isBatchTracked && (
                        <div className="flex gap-1 mb-1">
                          <input type="text" className="input text-xs w-24"
                            placeholder="batch #" value={l.batchNumber}
                            onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, batchNumber: e.target.value } : x))}
                          />
                          <input type="date" className="input text-xs w-32"
                            value={l.expiryDate}
                            onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, expiryDate: e.target.value } : x))}
                          />
                        </div>
                      )}
                      {l.isSerialTracked && (
                        <textarea
                          className="input text-xs font-mono" rows={2}
                          placeholder={`${Math.floor(l.receivedQty)} serial${l.receivedQty === 1 ? '' : 's'} required, space/newline separated`}
                          value={l.serialNumbersText}
                          onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, serialNumbersText: e.target.value } : x))}
                        />
                      )}
                      {!l.isBatchTracked && !l.isSerialTracked && <span className="text-xs text-ink-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4">
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Any receipt notes for the audit trail" />
        </div>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        {!canSubmit && <span className="text-xs text-ink-500 self-center mr-auto">{disabledReason}</span>}
        <button className="btn-primary" disabled={!canSubmit || createMut.isPending} onClick={() => createMut.mutate()}>
          {createMut.isPending ? 'Creating…' : 'Create draft GRN'}
        </button>
      </div>
    </Modal>
  );
}
